import { createSelector } from "reselect";
import get from "lodash/get";
import find from "lodash/find";
import filter from "lodash/filter";
import { PAYMENT_METHOD_CARD_TYPES, PAYMENT_STATUSES,PAYMENT_PARTNERS } from "utils/constants";
import { lower, upper } from "utils/helpers";
import { errorCodesSelector } from "./appSettings";
import isEmpty from "lodash/isEmpty";

import { workflowSelector } from "ducks/selectors";

const paymentRequestDomain = state => get(state, "api.paymentRequest.data", {});
const paymentOptionDomain = state =>
  get(state, "api.selectedPaymentOption.data", {});
const paymentDetailsDomain = state => get(state, "api.paymentDetails.data", {});
const processPaymentDomain = state => get(state, "api.processPayment.data", {});
const paymentStatusDomain = state => get(state, "api.paymentStatus.data", {});
const paymentRequestErrorCodeDomain = state => get(state, "api.paymentRequestError.data", {});
const paymentnotificationStatusUser = state =>
  get(state, "api.paymentnotificationStatus.data", {});
const enabledPaymentMethodsForUser = state =>
  get(state, "api.enabledPaymentMethods.data", {});

const savedPaymentMethodsForUser = state =>
  get(state, "api.savedPaymentMethods.data", []);

const savedPaymentCard = state =>
  get(state, "api.savedcardpayment.data.paymentMethods", []);

const paymentMethodId = state =>
  get(state, "api.savedcardpayment.data.paymentMethods", []);

const payUsingDomain = state => get(state, "api.payUsingMethod.data", null);

const ePWalletAuthenticateDomain = state =>
  get(state, "api.wallateAuthencticate.data.data", null);

const payUsingSavedCardDomain = state =>
  get(state, "api.savedPayUsingMethod.data", null);

const paymentEPErrorCodesDomain = state =>
  get(state, "api.easypaisaError.data", null);

const paymentTypeDomainDomain = state =>
  get(state, "api.thirdPartyPayment.data", null);

// ***************************************************************
// **************** paymentRequest selectors **********************
// ***************************************************************
export const paymentnotificationStatusSelector = createSelector(
  paymentnotificationStatusUser,
  paymentnotificationStatusData =>
    get(paymentnotificationStatusData, "paymentNotification", [])
);
export const enabledPaymentMethodsSelector = createSelector(
  enabledPaymentMethodsForUser,
  enabledPaymentMethodsData =>
    get(enabledPaymentMethodsData, "paymentMethods", [])
);

export const savedPaymentMethodsSelector = createSelector(
  savedPaymentMethodsForUser,
  savedPaymentMethodsData => get(savedPaymentMethodsData, "paymentMethods", [])
);

export const paymentOptionSelector = createSelector(
  paymentOptionDomain,
  paymentOption => paymentOption
);

export const paymentRequestSelector = createSelector(
  paymentRequestDomain,
  paymentRequest => paymentRequest
);

export const paymentDetailsSelector = createSelector(
  paymentDetailsDomain,
  paymentDetails => paymentDetails
);

export const processPaymentSelector = createSelector(
  processPaymentDomain,
  processPayment => processPayment
);

export const paymentStatusDetailsSelector = createSelector(
  paymentStatusDomain,
  statusDetails => statusDetails
);

export const paymentStatusSelector = createSelector(
  paymentStatusDomain,
  statusDetails => statusDetails?.payment_status || PAYMENT_STATUSES.PENDING
);

export const paymentStatusPendingSelector = createSelector(
  paymentStatusSelector,
  status => upper(status) === upper(PAYMENT_STATUSES.PENDING) || status === ""
);

export const paymentStatusPollingSelector = createSelector(
  paymentStatusDetailsSelector,
  statusDetails => {
    return (
      !isEmpty(statusDetails) &&
      (upper(statusDetails.payment_status) ===
        upper(PAYMENT_STATUSES.PENDING) ||
        statusDetails.payment_status === "")
    );
  }
);

export const paymentStatusSuccessSelector = createSelector(
  paymentStatusSelector,
  status => upper(status) === upper(PAYMENT_STATUSES.SUCCESS)
);

export const paymentStatusFailureSelector = createSelector(
  paymentStatusSelector,
  status =>
    ![
      upper(PAYMENT_STATUSES.SUCCESS),
      upper(PAYMENT_STATUSES.PENDING)
    ].includes(upper(status))
);

export const paymentErrorCodesSelector = createSelector(
  errorCodesSelector,
  errorCodes => get(errorCodes, "payment_service", {})
);

export const defaultPaymentSelector = createSelector(
  workflowSelector(),
  workflow => {
    const currentWorkflow = find(
      workflow,
      page => page.path === window.location.pathname
    );
    return get(currentWorkflow, "defaultPayment", "");
  }
);

export const fetchSavedCardOption = createSelector(
  savedPaymentCard,
  cardDetails => {
    const cardData = cardDetails.filter(
      card =>
        (card?.cardType === PAYMENT_METHOD_CARD_TYPES.CARD &&
          card?.default === true &&
          card?.expired === false) ||
        (card?.cardType === PAYMENT_METHOD_CARD_TYPES.WALLET &&
          !isEmpty(card?.instrumentId) &&
          card?.expired === false)
    );
    return cardData.length ? cardData : [];
  }
);

export const fetchPaymentIds = createSelector(
  paymentMethodId,
  paymentMethodIds => {
    let instrumentId = filter(paymentMethodIds, card => !card?.instrumentId);
    return instrumentId ? [instrumentId] : [];
  }
);

export const getSelectedPaymentId = createSelector(
  payUsingDomain,
  payUsingInfo => {
    return payUsingInfo && payUsingInfo.payment_id;
  }
);

export const payUsingInfoSelector = createSelector(
  payUsingDomain,
  payUsingInfo => {
    return payUsingInfo;
  }
);

export const paymentMethodIdSelector = createSelector(
  paymentMethodId,
  payUsingInfoSelector,
  paymentOptionSelector,
  (paymentMethodIdInfo, payUsingInfo, selectedPaymentInfo) => {
    const getPayUsingPaymentId = info => {
      return !isEmpty(info?.instrument_id) ? info?.payment_partner : null;
    };

    let paymentPartner =
      selectedPaymentInfo?.payment_partner ||
      getPayUsingPaymentId(payUsingInfo) ||
      null;

    const paymentId = !isEmpty(paymentMethodIdInfo)
      ? paymentMethodIdInfo.find(methodInfo =>
          lower(methodInfo?.name).includes(lower(paymentPartner))
        )
      : null;

    return paymentId?.id;
  }
);

export const epWalletAuthenticateInfoSelector = createSelector(
  ePWalletAuthenticateDomain,
  ePWalletAuthenticateInfo => {
    return ePWalletAuthenticateInfo;
  }
);

export const payUsingInfoSelectedSavedCard = createSelector(
  payUsingSavedCardDomain,
  payUsingSavedCardInfo => {
    return payUsingSavedCardInfo;
  }
);

export const paymentEPErrorCodesSelector = createSelector(
  paymentEPErrorCodesDomain,
  paymentEPErrorCodes => {
    return paymentEPErrorCodes;
  }
);

export const paymentStatusErrorCodeSelector = createSelector(
  paymentStatusDomain,
  paymentStatus => {
    return !isEmpty(paymentStatus?.error_code) ? paymentStatus?.error_code : ""
  } 
)

export const paymentRequestErrorCodeSelector = createSelector(
  paymentRequestErrorCodeDomain,
  paymentRequestErr => {
    return !isEmpty(paymentRequestErr) ? paymentRequestErr : {}
  } 
)

export const paymentTypeSelector = createSelector(
  paymentTypeDomainDomain,
  paymentType => {
    return paymentType || false
  } 
)

export const getDisabledPaymentSelector = createSelector(
  paymentMethodId,
  paymentMethodIds => {
    // Filter out payment methods without instrumentId
    const instrumentId = filter(paymentMethodIds, card => !card?.instrumentId);

    const savedinstrumentId = filter(paymentMethodIds, card => card?.instrumentId);
    const easypaisaSaved = savedinstrumentId.some(card => 
      card.name.toLowerCase() === PAYMENT_PARTNERS.EASYPAISA_WALLET
    );  
    const paymentMethods = Object.values(instrumentId);
    let paymentMethod = paymentMethods
      .filter(obj => obj.isEnabled === false) // Only disabled methods
    
    if (easypaisaSaved) {
      paymentMethod = paymentMethod.filter(method => method.name.toLowerCase() !== PAYMENT_PARTNERS.EASYPAISA_WALLET);
    }  
     const isDisabled = paymentMethod.length >= 1
      return isDisabled
    }
  );

export const getSavedInstrumentStatusSelector = createSelector(
  paymentMethodId,
  paymentMethodIds => {
    // Filter out payment methods with instrumentId
    const savedCards = filter(paymentMethodIds, card => card?.instrumentId);
    const paymentMethods = Object.values(savedCards);
    const paymentDisabledMethod = paymentMethods
      .filter(obj => obj.isEnabled === false) // Only disabled methods
    const isAllSavedInstrumentDisabled = paymentDisabledMethod.length === savedCards.length
    return isAllSavedInstrumentDisabled
  }
);

// ***************************************************************
// *************** paymentRequest makeSelect *********************
// ***************************************************************

export const makeSelectPaymentRequest = () =>
  createSelector(paymentRequestSelector, paymentRequest => paymentRequest);
export const makeSelectDefaultPayment = () => defaultPaymentSelector;
export const makeSelectPaymentDetails = () => paymentDetailsSelector;
export const makeSelectProcessPayment = () => processPaymentSelector;
export const makeSelectPaymentStatusPending = () =>
  paymentStatusPendingSelector;
export const makeSelectPaymentStatusSuccess = () =>
  paymentStatusSuccessSelector;
export const makeSelectPaymentStatusFailure = () =>
  paymentStatusFailureSelector;
export const makeSelectPaymentStatus = () => paymentStatusSelector;
export const makeSelectPaymentErrorCodes = () => paymentErrorCodesSelector;
export const makeSelectPaymentStatusPolling = () =>
  paymentStatusPollingSelector;
export const makeSelectEnabledPaymentMethods = () =>
  enabledPaymentMethodsSelector;
export const makeSelectSavedPaymentMethods = () => savedPaymentMethodsSelector;
export const makePaymentnotificationStatus = () =>
  paymentnotificationStatusSelector;
export const makeSelectPaymentOption = () => paymentOptionSelector;
export const makeSelectSavedCardOption = () => fetchSavedCardOption;
export const makeSelectSavedPaymentIds = () => fetchPaymentIds;
export const makeSelectGetPaymentId = () => getSelectedPaymentId;
export const makeSelectPayUsingInfo = () => payUsingInfoSelector;
export const makeSelectPaymentMethodId = () => paymentMethodIdSelector;
export const makeSelectEpWalletAuthenticateInfo = () =>
  epWalletAuthenticateInfoSelector;
export const makeSelectRetrieveSavedCardDetails = () =>
  payUsingInfoSelectedSavedCard;
export const makeSelectRetrieveEPErroCodes = () => paymentEPErrorCodesSelector;
export const makeSelectPaymentStatusErrorCode = () =>
  paymentStatusErrorCodeSelector;
export const makeSelectPaymentRequestErrorCode = () =>
  paymentRequestErrorCodeSelector;
export const makeSelectPaymentType = () =>
  paymentTypeSelector;
export const makeSelectIsDisablePayment = () =>
  getDisabledPaymentSelector;
export const makeSelectAllSavedInstrumentStatus = () => getSavedInstrumentStatusSelector;

